<script setup lang="ts">
import { ref, computed } from 'vue'
import { RecaptchaV2 } from "vue3-recaptcha-v2";

const props = defineProps<{
  data?: Record<string, any>
  confirmation?: any,
  user?: any,
  company?: any
}>()

const emit = defineEmits<{
  (e: 'complete', data: any): void
}>()

// Extract company and user data from props with default values
const companyData = computed(() => {
  return {
    company_name: props.company?.company_name || '',
    business_types_id: props.company?.business_types_id || '',
    countrys_id: props.company?.countrys_id || '',
    number_of_employees: props.company?.number_of_employees || '',
    primary_contact_person: props.company?.primary_contact_person || '',
    primary_email: props.company?.primary_email || '',
    primary_contact_number: props.company?.primary_contact_number || '',
    phone_country_code: props.company?.phone_country_code || '',
    no_of_outlets: props.company?.no_of_outlets || '',
    require_accounts: props.company?.require_accounts || false,
    require_crm: props.company?.require_crm || false,
    require_hrm: props.company?.require_hrm || false,
    contact_us: props.company?.contact_us || false,
    referred_by: props.company?.referred_by || '',
    remarks: props.company?.remarks || '',
    packages_id: props.company?.packages_id || 'trial',
    website: props.company?.website || '',
    industry: props.company?.industry || '',
    employees: props.company?.employees || '',
    referral: props.company?.referral || '',
    profile_photo: props.user?.profile_photo || null,
    name: props.user?.name || '',
    email: props.user?.email || '',
    password: props.user?.password || '',
    confirm_password: props.user?.confirm_password || '',
    mobile_number: props.user?.mobile_number || '',
    country_code: props.user?.country_code || 'US',
    cover_photo: props.user?.cover_photo || null,
    profile_text: props.user?.profile_text || ''
  }
})

const captchaToken = ref('')
const canAcceptTerms = ref(false)
const termsContainer = ref<HTMLElement | null>(null)

const confirmations = ref({
  information: false,
  terms: false,
  paidSupport: false,
  newsletter: true // Default checked
})

const termsAndConditions = `
  <h4 class="text-lg font-medium mb-4">Terms and Conditions</h4>
  Terms and ConditionsElly Faseyha POS - Cloud-Based POS, Inventory, CRM & Accounting Software

Effective Date: January 22, 2025

Welcome to Elly Investments’ cloud-based software. These Terms and Conditions govern your use of our Point of Sale (POS), Inventory Management, Customer Relationship Management (CRM), and Accounting software. By accessing or using our services, you agree to comply with these terms. If you do not agree, please do not use our services.

1. Definitions

"Company" refers to Elly Investments, registered under the laws of the Republic of Maldives.

"User" refers to any individual or entity using our software.

"Services" refers to our cloud-based software, including POS, Inventory, CRM, and Accounting features.

"Confidential Information" refers to business data, customer details, financial records, and other sensitive information.

2. User Obligations

Users must provide accurate business information when registering.

Users are responsible for maintaining the confidentiality of their login credentials.

Users shall not use the software for any fraudulent, illegal, or unauthorized purpose.

Users must comply with all applicable Maldivian laws, including the Electronic Transactions Act and consumer protection laws.

3. Data Protection & Privacy

Elly Investments complies with the Maldivian Personal Data Protection Act (if enacted) and ensures secure handling of user data.

Users consent to the collection, processing, and storage of their business and customer data.

Data will not be shared with third parties unless required by law or with explicit user consent.

4. Subscription & Payment

Our software operates on a subscription model with different pricing plans.

Subscription fees are non-refundable unless specified otherwise.

Non-payment may result in suspension or termination of services.

5. Intellectual Property

All software content, including trademarks, logos, and proprietary algorithms, belongs to Elly Investments.

Users may not copy, modify, distribute, or reverse-engineer the software.

6. Limitation of Liability

Elly Investments does not guarantee uninterrupted or error-free service.

We are not responsible for any loss of data, business interruptions, or financial loss resulting from software use.

Users assume full responsibility for any consequences arising from incorrect data input or misuse.

7. Service Availability & Termination

We reserve the right to modify, suspend, or discontinue any part of the service.

Users may terminate their account by notifying us in writing.

We may terminate accounts without notice if users violate these terms.

8. Governing Law & Dispute Resolution

These Terms and Conditions are governed by the laws of the Republic of Maldives.

Any disputes shall be resolved through arbitration or in Maldivian courts.

9. Amendments

We reserve the right to modify these terms at any time. Users will be notified of significant changes.

Continued use of our software after changes constitutes acceptance of the revised terms.

10. Contact Information

For any questions regarding these Terms and Conditions, please contact us at: info@ellyinvestments.com

By using our software, you acknowledge that you have read, understood, and agreed to these Terms and Conditions.`

const isValid = computed(() => {
  return confirmations.value.information && 
         confirmations.value.terms && 
         captchaToken.value
})

const canSubmit = computed(() => {
  return isValid.value && confirmations.value.terms
})

const handleTermsScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const isAtBottom = Math.abs(
    element.scrollHeight - element.clientHeight - element.scrollTop
  ) < 1
  
  if (isAtBottom) {
    canAcceptTerms.value = true
  }
}

const handleCaptchaVerify = (token: unknown) => {
  captchaToken.value = token as string
}

const handleSubmit = () => {
  if (!canSubmit.value) return

  emit('complete', {
    ...confirmations.value,
    captchaToken: captchaToken.value
  })
}

// Business Types
const businessTypes = [
  { id: '1', name: 'Sole Proprietorship' },
  { id: '2', name: 'Partnership' },
  { id: '3', name: 'Corporation' },
  { id: '4', name: 'Limited Liability Company (LLC)' },
  { id: '5', name: 'Non-Profit Organization' }
]

// Countries with flags and phone codes
const countries = [
  { id: '1', name: 'Maldives', flag: 'MV', code: '+960' },
]

</script>

<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-6">Review Information</h2>

    <!-- Company Information -->
    <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Company Information</h3>
      <div class="grid md:grid-cols-2 gap-4">
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Company Name</p>
          <p class="font-medium">{{ companyData.company_name }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Contact No</p>
          <p class="font-medium">{{ companyData.primary_contact_number }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Business Type</p>
          <p class="font-medium">{{ businessTypes.find((e) => e.id == companyData.business_types_id)?.name }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">No Of Outlets</p>
          <p class="font-medium">{{ companyData.no_of_outlets }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">No of Employees</p>
          <p class="font-medium">{{ companyData.number_of_employees }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Primary Contact Person</p>
          <p class="font-medium">{{ companyData.primary_contact_person }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Primary Email</p>
          <p class="font-medium">{{ companyData.primary_email }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Country</p>
          <p class="font-medium">{{ countries.find((e) => e.id == companyData.countrys_id)?.name }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Package</p>
          <p class="font-medium">{{ companyData.packages_id }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">More Info </p>
          <p class="font-medium" v-if="companyData.require_accounts == true">Require Accounts Module</p>
          <p class="font-medium" v-if="companyData.require_crm == true">Require CRM Module</p>
          <p class="font-medium" v-if="companyData.require_hrm == true">Require HRM Module</p>
          <p class="font-medium" v-if="companyData.contact_us == true">Contact me about the product</p>
        </div>
      </div>
    </div>

    <!-- User Information -->
    <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">User Information</h3>
      <div class="grid grid-cols-2 gap-4">
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Name</p>
          <p class="font-medium">{{ companyData.name }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Email</p>
          <p class="font-medium">{{ companyData.email }}</p>
        </div>
        <div>
          <p class="text-sm text-gray-500 dark:text-gray-400">Mobile Number</p>
          <p class="font-medium">{{ companyData.mobile_number }}</p>
        </div>
      </div>
    </div>

    <!-- Terms and Conditions -->
    <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Terms and Conditions</h3>
      <div class="h-48 overflow-y-auto mb-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700" ref="termsContainer" @scroll="handleTermsScroll">
        <div v-html="termsAndConditions"></div>
      </div>
    </div>

    <!-- Checkboxes -->
    <div class="space-y-4">
      <!-- Information Confirmation -->
      <label class="flex items-start gap-3">
        <input
          type="checkbox"
          v-model="confirmations.information"
          class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
          required
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">
          I confirm that all the information provided above is correct and accurate
        </span>
      </label>

      <!-- Terms Agreement -->
      <label class="flex items-start gap-3" :class="{ 'opacity-50': !canAcceptTerms }">
        <input
          type="checkbox"
          v-model="confirmations.terms"
          class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
          :disabled="!canAcceptTerms"
          required
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">
          I have read and agree to the Terms and Conditions
        </span>
      </label>

      <!-- Paid Support -->
      <label class="flex items-start gap-3">
        <input
          type="checkbox"
          v-model="confirmations.paidSupport"
          class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">
          I would like to receive paid support to help set up the system
        </span>
      </label>

      <!-- Newsletter -->
      <label class="flex items-start gap-3">
        <input
          type="checkbox"
          v-model="confirmations.newsletter"
          class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">
          Subscribe to our newsletter for updates and tips
        </span>
      </label>
    </div>

    <!-- reCAPTCHA -->
    <div class="flex justify-center">
      <RecaptchaV2
        @load-callback="handleCaptchaVerify"
      />
    </div>

    <!-- Submit Button -->
    <div class="flex justify-end">
      <button
        type="submit"
        class="px-6 py-2 bg-blue-600 text-white rounded-lg transition-colors"
        :class="[
          canSubmit
            ? 'hover:bg-blue-700'
            : 'opacity-50 cursor-not-allowed'
        ]"
        :disabled="!canSubmit"
      >
        Complete Registration
      </button>
    </div>
  </form>
</template>
```